import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useAuth from '../../../hooks/useAuth';
import useSnackbarStore from '../../../store/snackbar';

// TODO: point at your payroll apiService
import { getTimeClockStatus, clockIn, clockOut } from '../../Settings/Payroll/apiService';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const LONG_SHIFT_MS = 12 * 60 * 60 * 1000;

export const timeClockKey = (userId) => ['timeclock', 'status', userId];

const toMs = (d) => (d ? new Date(d).getTime() : null);

const fmtClock = (d) =>
  new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// 3:42:07
const fmtTimer = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// 3h 42m
const fmtDuration = (ms) => {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  return `${Math.floor(totalMin / 60)}h ${String(totalMin % 60).padStart(2, '0')}m`;
};

// Ticks only while `active`, so nothing re-renders when clocked out
const useNow = (intervalMs, active) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, active]);
  return now;
};

const getStatus = (openEntry, now) => {
  if (!openEntry) return 'out';
  return now - toMs(openEntry.clock_in_at) > LONG_SHIFT_MS ? 'long' : 'in';
};

/* ------------------------------------------------------------------ */
/* Shared hook (same query key everywhere, so react-query dedupes)     */
/* ------------------------------------------------------------------ */

/**
 * Expected getTimeClockStatus response:
 * {
 *   enabled:       boolean,  // user has a pay rate covering today (or still has an open shift to close)
 *   open_entry:    { time_entry_id, clock_in_at } | null,   // current open shift (may have started yesterday)
 *   today_entries: [{ time_entry_id, clock_in_at, clock_out_at }]  // today's closed entries
 * }
 */
export const useTimeClock = () => {
  const { user } = useAuth();
  const userId = user?.user_id;
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);

  const query = useQuery({
    queryKey: timeClockKey(userId),
    queryFn: () => getTimeClockStatus(userId),
    enabled: !!userId,
    staleTime: 30 * 1000, // header + menu share this; don't refetch every time the menu opens
  });

  const punch = useMutation({
    mutationFn: (action) => (action === 'in' ? clockIn() : clockOut()),
    onSuccess: (_res, action) => {
      showSnackbar(action === 'in' ? 'Clocked in' : 'Clocked out', 'success');
      queryClient.invalidateQueries({ queryKey: timeClockKey(userId) });
    },
    onError: (err) => {
      showSnackbar(err?.response?.data?.message || 'Could not update the time clock', 'error');
      // Server state may differ from ours (e.g. clocked in from another tab) -> resync
      queryClient.invalidateQueries({ queryKey: timeClockKey(userId) });
    },
  });

  return {
    userId,
    // Nothing renders until we know the user is on payroll (no flash for non-payroll users)
    enabled: query.data?.enabled === true,
    openEntry: query.data?.open_entry ?? null,
    todayEntries: query.data?.today_entries ?? [],
    clockIn: () => punch.mutate('in'),
    clockOut: () => punch.mutate('out'),
    clockOutAsync: () => punch.mutateAsync('out'),
    isPunching: punch.isPending,
  };
};

/* ------------------------------------------------------------------ */
/* Menu section: status, live timer, clock in/out button               */
/* ------------------------------------------------------------------ */

const tint = (key) => (theme) => alpha(theme.palette[key].main, 0.14);

const STATUS_PILL = {
  out: { label: 'Not clocked in', color: 'text.secondary', bg: 'action.hover' },
  in: { label: 'Clocked in', color: 'success.dark', bg: tint('success') },
  long: { label: 'Long shift', color: 'warning.dark', bg: tint('warning') },
};

const StatusPill = ({ status }) => {
  const { label, color, bg } = STATUS_PILL[status];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.6rem',
        px: '1rem',
        py: '0.3rem',
        borderRadius: '1.2rem',
        fontSize: '1.2rem',
        fontWeight: 600,
        color,
        bgcolor: bg,
        '&::before': {
          content: '""',
          width: '0.7rem',
          height: '0.7rem',
          borderRadius: '50%',
          bgcolor: 'currentColor',
        },
      }}
    >
      {label}
    </Box>
  );
};

const EntryList = ({ entries }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: '1rem 0 0', p: 0 }}>
    {entries.map((e) => (
      <Box
        component="li"
        key={e.time_entry_id}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: '0.3rem',
          fontSize: '1.3rem',
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{fmtClock(e.clock_in_at)} – {fmtClock(e.clock_out_at)}</span>
        <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {fmtDuration(toMs(e.clock_out_at) - toMs(e.clock_in_at))}
        </Box>
      </Box>
    ))}
  </Box>
);

export default function TimeClock() {
  const { enabled, openEntry, todayEntries, clockIn, clockOut, isPunching } = useTimeClock();
  const now = useNow(1000, !!openEntry);

  // No pay rate set up -> time clock isn't available for this user
  if (!enabled) return null;

  const status = getStatus(openEntry, now);
  const openMs = openEntry ? now - toMs(openEntry.clock_in_at) : 0;
  const closedMs = todayEntries.reduce(
    (sum, e) => sum + (toMs(e.clock_out_at) - toMs(e.clock_in_at)),
    0
  );
  const totalMs = closedMs + openMs;

  return (
    <>
      {/* Menu closes on any click; keep it open so the user sees the result */}
      <Box onClick={(e) => e.stopPropagation()} sx={{ px: '1.8rem', py: '1.6rem', width: '32rem', maxWidth: '100%', whiteSpace: 'normal' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '1rem' }}>
          <Typography
            sx={{ fontSize: '1.2rem', fontWeight: 600, letterSpacing: '0.08rem', textTransform: 'uppercase', color: 'text.secondary' }}
          >
            Time clock
          </Typography>
          <StatusPill status={status} />
        </Box>

        {openEntry ? (
          <>
            <Typography sx={{ fontSize: '3.2rem', fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtTimer(openMs)}
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', color: 'text.secondary', mt: '0.4rem' }}>
              Since {fmtClock(openEntry.clock_in_at)}
              {todayEntries.length > 0 && ` · ${fmtDuration(totalMs)} today`}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '1.4rem', color: 'text.secondary' }}>
            {todayEntries.length > 0 ? (
              <>Today: <Box component="b" sx={{ color: 'text.primary' }}>{fmtDuration(totalMs)}</Box></>
            ) : (
              'No time recorded today.'
            )}
          </Typography>
        )}

        {status === 'long' && (
          <Box
            sx={{
              mt: '1rem',
              p: '0.8rem 1rem',
              borderRadius: '0.6rem',
              fontSize: '1.3rem',
              lineHeight: 1.4,
              color: 'warning.dark',
              bgcolor: tint('warning'),
            }}
          >
            You've been clocked in over 12 hours. If you forgot to clock out, clock out now and ask a
            manager to fix the time.
          </Box>
        )}

        {todayEntries.length > 0 && <EntryList entries={todayEntries} />}

        <Button
          fullWidth
          variant="contained"
          disableElevation
          color={openEntry ? 'primary' : 'success'}
          disabled={isPunching}
          onClick={openEntry ? clockOut : clockIn}
          startIcon={
            isPunching ? (
              <CircularProgress size={16} color="inherit" />
            ) : openEntry ? (
              <StopRoundedIcon />
            ) : (
              <AccessTimeRoundedIcon />
            )
          }
          sx={{ mt: '1.4rem', height: '4.4rem', fontSize: '1.5rem', fontWeight: 600, textTransform: 'none' }}
        >
          {openEntry ? 'Clock out' : 'Clock in'}
        </Button>
      </Box>
      <Divider />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Header pieces                                                       */
/* ------------------------------------------------------------------ */

// Running timer pill shown next to the avatar while clocked in
export function TimeClockChip() {
  const { enabled, openEntry } = useTimeClock();
  const now = useNow(30000, !!openEntry);
  if (!enabled || !openEntry) return null;

  const status = getStatus(openEntry, now);
  const ms = now - toMs(openEntry.clock_in_at);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.8rem',
        height: '3.4rem',
        px: '1.2rem',
        borderRadius: '1.7rem',
        fontSize: '1.4rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: status === 'long' ? 'warning.dark' : 'success.dark',
        bgcolor: tint(status === 'long' ? 'warning' : 'success'),
        '@keyframes tcPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
      }}
    >
      <Box
        component="span"
        sx={{ width: '0.8rem', height: '0.8rem', borderRadius: '50%', bgcolor: 'currentColor', animation: 'tcPulse 2s infinite' }}
      />
      {h}:{String(m).padStart(2, '0')}
    </Box>
  );
}

// Green/amber dot on the avatar while clocked in
export function TimeClockBadge({ children }) {
  const { enabled, openEntry } = useTimeClock();
  const now = useNow(60000, !!openEntry);
  const status = enabled ? getStatus(openEntry, now) : 'out';

  return (
    <Badge
      overlap="circular"
      variant="dot"
      invisible={status === 'out'}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        '& .MuiBadge-dot': {
          width: '1.3rem',
          height: '1.3rem',
          borderRadius: '50%',
          border: '2px solid #fff',
          bgcolor: status === 'long' ? 'warning.main' : 'success.main',
        },
      }}
    >
      {children}
    </Badge>
  );
}